import {MATRIX4, QUATERNION, VECTOR3} from '../../index.js';

/**
 * Creates Hypercube.js nodes.
 * @example
 * const node = new Node(name);
 */
class Node {

    /**
     * Stores the audio element.
     * @type {HTMLAudioElement}
     * @protected
     */
    $audio;

    /**
     * Stores the camera.
     * @type {import('../../index.js').Camera}
     * @private
     */
    $camera;

    /**
     * Stores the components of the node.
     * @type {object}
     * @private
     */
    $components;

    /**
     * Stores the world matrix of the node.
     * @type {matrix4}
     * @private
     */
    $matrix;

    /**
     * Stores the meshes.
     * @type {import('../../index.js').Mesh[]}
     * @private
     */
    $meshes;

    /**
     * Stores the name.
     * @type {string}
     * @private
     */
    $name;

    /**
     * Stores the child nodes.
     * @type {Node[]}
     * @private
     */
    $nodes;

    /**
     * Stores the parent node.
     * @type {Node}
     * @private
     */
    $parent;

    /**
     * Stores the script.
     * @type {import('../../index.js').Script}
     * @private
     */
    $script;

    /**
     * Stores the UUID.
     * @type {string}
     * @private
     */
    $uuid;

    /**
     * Gets the audio element.
     * @type {HTMLAudioElement}
     * @public
     * @readonly
     */
    get audio() {

        return this.$audio;
    };

    /**
     * Gets the camera.
     * @type {import('../../index.js').Camera}
     * @public
     * @readonly
     */
    get camera() {

        return this.$camera;
    }

    /**
     * Gets the world matrix of the node.
     * @type {matrix4}
     * @public
     * @readonly
     */
    get matrix() {

        return this.$matrix;
    }

    /**
     * Gets the world matrix inverse of the node.
     * @type {matrix4}
     * @public
     * @readonly
     */
    get matrixinverse() {

        return MATRIX4.invert(this.$matrix);
    }

    /**
     * Gets the meshes.
     * @type {import('../../index.js').Mesh[]}
     * @public
     * @readonly
     */
    get meshes() {

        return this.$meshes;
    }

    /**
     * Gets the name.
     * @type {string}
     * @public
     * @readonly
     */
    get name() {

        return this.$name;
    }

    /**
     * Gets the child nodes.
     * @type {Node[]}
     * @public
     * @readonly
     */
    get nodes() {

        return this.$nodes;
    }

    /**
     * Gets the parent node.
     * @type {Node}
     * @public
     * @readonly
     */
    get parent() {

        return this.$parent;
    }

    /**
     * Gets the orientation of the node.
     * @type {quaternion}
     * @public
     * @readonly
     */
    get rotation() {

        return MATRIX4.getrotation(this.$matrix);
    }

    /**
     * Gets the script.
     * @type {import('../../index.js').Script}
     * @public
     * @readonly
     */
    get script() {

        return this.$script;
    }

    /**
     * Gets the position of the node.
     * @type {vector3}
     * @public
     * @readonly
     */
    get translation() {

        return MATRIX4.gettranslation(this.$matrix);
    }

    /**
     * Gets the UUID.
     * @type {string}
     * @public
     * @readonly
     */
    get uuid() {

        return this.$uuid;
    }

    /**
     * Creates a new Hypercube.js node.
     * @param {string} [$name] The name of the node.
     */
    constructor($name = 'Node') {

        this.$name = $name;

        this.$components = {};
        this.$matrix = MATRIX4.identity();
        this.$meshes = [];
        this.$nodes = [];
    }

    /**
     * Updates the world matrix of the child nodes.
     * @param {matrix4} $matrixtransformation The transformation matrix to apply to children.
     * @private
     */
    $updatechildren($matrixtransformation) {

        this.nodes.forEach(($node) => {

            $node.$matrix = MATRIX4.multiply($matrixtransformation, $node.$matrix);
            $node.$updatechildren($matrixtransformation);
        });
    }

    /**
     * Adds a child node.
     * @param {Node} $node The child node to add.
     * @public
     */
    addchild($node) {

        this.$nodes.push($node);

        $node.setparent(this);
    }

    /**
     * Adds a mesh.
     * @param {import('../../index.js').Mesh} $mesh The mesh to add.
     * @public
     */
    addmesh($mesh) {

        this.$meshes.push($mesh);
    }

    /**
     * Gets a component of the node.
     * @param {string} $name The name of the component to get.
     * return {any}
     * @public
     */
    getcomponent($name) {

        return this.$components[$name];
    }

    /**
     * Checks if the node has given component.
     * @param {string} $name The name of the component to check.
     * return {boolean}
     * @public
     */
    hascomponent($name) {

        return this.$components.hasOwnProperty($name);
    }

    /**
     * Looks at target position in the world space from a third person point of view.
     * @param {vector3} $vector3target The target position to look at.
     * @param {vector3} $vector3up The up vector.
     * @public
     */
    lookat($vector3target, $vector3up) {

        const matrix = MATRIX4.lookat(this.$matrix, $vector3target, $vector3up)

        const transformation = MATRIX4.multiply(matrix, MATRIX4.invert(this.$matrix));

        this.$matrix = matrix;

        this.$updatechildren(transformation);
    }

    /**
     * Removes a child node.
     * @param {Node} $node The child node to remove.
     * @public
     */
    removechild($node) {

        this.$nodes.splice(this.$nodes.indexOf($node), 1);

        $node.unsetparent();
    }

    /**
     * Removes all child nodes.
     * @public
     */
    removechildren() {

        while (this.$nodes.length > 0) {

            this.removechild(this.$nodes.shift());
        }
    }

    /**
     * Removes a component of the node.
     * @param {string} $name The name of the component to remove.
     * @public
     */
    removecomponent($name) {

        delete this.$components[$name];
    }

    /**
     * Rotates the node in the world space from a third person point of view.
     * @param {quaternion} $quaternion The quaternion representing the rotation to apply.
     * @public
     */
    rotate($quaternion) {

        let matrix = MATRIX4.identity();

        matrix = MATRIX4.translate(matrix, MATRIX4.gettranslation(this.$matrix));
        matrix = MATRIX4.multiply(matrix, MATRIX4.fromquaternion(QUATERNION.normalize($quaternion)));
        matrix = MATRIX4.multiply(matrix, MATRIX4.fromquaternion(MATRIX4.getrotation(this.$matrix)));

        const transformation = MATRIX4.multiply(matrix, MATRIX4.invert(this.$matrix));

        this.$matrix = matrix;

        this.$updatechildren(transformation);
    }

    /**
     * Rotates the node in the world space from a first person point of view.
     * @param {quaternion} $quaternion The quaternion representing the rotation to apply.
     * @public
     */
    rotateself($quaternion) {

        const matrix = MATRIX4.multiply(this.$matrix, MATRIX4.fromquaternion(QUATERNION.normalize($quaternion)));

        const transformation = MATRIX4.multiply(matrix, MATRIX4.invert(this.$matrix));

        this.$matrix = matrix;

        this.$updatechildren(transformation);
    }

    /**
     * Sets the audio element.
     * @param {HTMLAudioElement} $audio The audio element to set.
     * @public
     */
    setaudio($audio) {

        this.$audio = $audio;
    }

    /**
     * Sets the camera.
     * @param {import('../../index.js').Camera} $camera The camera to set.
     * @public
     */
    setcamera($camera) {

        this.$camera = $camera;
    }

    /**
     * Sets a component of the node.
     * @param {string} $name The name of the component to set.
     * @param {any} $component The value of the component to set.
     * @public
     */
    setcomponent($name, $component) {

        this.$components[$name] = $component;
    }

    /**
     * Sets the parent node.
     * @param {Node} $node The parent node to set.
     * @public
     */
    setparent($node) {

        this.$parent = $node;
    }

    /**
     * Sets the script of the node.
     * @param {import('../../index.js').Script} $script The script to set.
     * @public
     */
    setscript($script) {

        this.$script = $script;
    }

    /**
     * Sets the UUID of the node.
     * @param {string} $uuid The UUID to set.
     * @public
     */
    setuuid($uuid) {

        this.$uuid = $uuid;
    }

    /**
     * Translates the node in the world space from a third person point of view.
     * @param {vector3} $vector3 The translation to apply.
     * @public
     */
    translate($vector3) {

        let matrix = MATRIX4.identity();

        matrix = MATRIX4.translate(matrix, $vector3);
        matrix = MATRIX4.translate(matrix, MATRIX4.gettranslation(this.$matrix));
        matrix = MATRIX4.multiply(matrix, MATRIX4.fromquaternion(MATRIX4.getrotation(this.$matrix)));

        const transformation = MATRIX4.multiply(matrix, MATRIX4.invert(this.$matrix));

        this.$matrix = matrix;

        this.$updatechildren(transformation);
    }

    /**
     * Translates the node in the world space from a first person point of view.
     * @param {vector3} $vector3 The translation to apply.
     * @public
     */
    translateself($vector3) {

        const matrix = MATRIX4.translate(this.$matrix, $vector3)

        const transformation = MATRIX4.multiply(matrix, MATRIX4.invert(this.$matrix));

        this.$matrix = matrix;

        this.$updatechildren(transformation);
    }

    /**
     * Translates the node in the world space to given position.
     * @param {vector3} $vector3 The position to translate to.
     * @public
     */
    translateto($vector3) {

        this.translate(VECTOR3.subtract($vector3, MATRIX4.gettranslation(this.$matrix)));
    }

    /**
     * Unsets the parent node.
     * @public
     */
    unsetparent() {

        delete this.$parent;
    }
}

export {

    Node
};

export default Node;
